/**
 * §7.4 庫存總覽。與 mockup 的差異：**加分頁與搜尋**（§8.1 ?limit=&cursor=）。
 */

import { useState } from 'react'
import { download, withQuery } from '../api/client.js'
import { useQuery } from '../api/hooks.js'
import { HomogeneityBadge, ResolutionBadge } from '../components/badges.js'
import { Button, Card, Empty, Mono, Pill, Table, Td, TextInput, Select } from '../components/ui.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import { kgUnit, count } from '../../../shared/lib/format.js'
import { Link } from '../router.js'
import { Can } from '../auth/AuthContext.js'

interface Item {
  item_id: string
  material_code: string
  container_id: string | null
  location_id: string | null
  net_kg: string | null
  panel_count: number | null
  resolution_grade: 'R1' | 'R2' | 'R3' | 'R4'
  homogeneity_grade: 'A' | 'B' | 'C'
  status: string
  work_order_id: string | null
  r3_taint: boolean
}

export function Inventory() {
  const dict = useDict()
  const { config } = useConfig()
  // §9 規則 7：下拉選項的中文從料號字典來。只列本階段有在用的物料
  const materials = (config?.materialDict ?? []).filter((m) => m.activeInMockup)
  const [q, setQ] = useState('')
  const [material, setMaterial] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)
  const [pages, setPages] = useState<Item[][]>([])

  const path = withQuery('/api/inventory/items', { q, materialCode: material, cursor, limit: 50 })
  const { data, loading, error } = useQuery<{ items: Item[]; nextCursor: string | null }>(path)

  const items = [...pages.flat(), ...(data?.items ?? [])]
  const totalKg = items.reduce((a, x) => a + (x.net_kg === null ? 0 : Number(x.net_kg)), 0)
  const noWeight = items.filter((x) => x.net_kg === null).length

  const resetPaging = (fn: () => void) => { setPages([]); setCursor(null); fn() }

  return (
    <Card
      title="庫存總覽"
      subtitle={`在庫 ${count(items.length)} 個${dict.term('item')}${
        data?.nextCursor ? '（還有更多）' : ''}`}
      right={
        <Can perm="inventory.read">
          <Button size="sm" onClick={() => download('/api/inventory/items', { q, materialCode: material, format: 'csv' })}>
            下載 CSV
          </Button>
        </Can>
      }
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <TextInput
          placeholder="搜尋料件號"
          value={q}
          onChange={(e) => resetPaging(() => setQ(e.target.value))}
          className="max-w-[240px]"
        />
        <Select value={material} onChange={(e) => resetPaging(() => setMaterial(e.target.value))}>
          <option value="">全部物料</option>
          {/* §9 規則 7：選項的中文從料號字典來 */}
          {materials.map((m) => (
            <option key={m.materialCode} value={m.materialCode}>{m.label}</option>
          ))}
        </Select>
      </div>

      {noWeight > 0 && (
        <p className="mb-2 text-xs text-amber-700">
          其中 {noWeight} 個料件沒有重量帳（進庫未過磅），合計重量不含它們。
        </p>
      )}

      {error && <p className="text-sm text-red-700">{error.message}</p>}
      {items.length === 0 && !loading && <Empty>目前沒有在庫料件。</Empty>}

      {items.length > 0 && (
        <Table head={['料件號', '物料', '重量', '片數', '解析度', '同質性', '儲位', '容器']}>
          {items.map((it) => (
            <tr key={it.item_id} className="hover:bg-ink-50">
              <Td><Mono>{it.item_id}</Mono></Td>
              <Td>{dict.material(it.material_code)}</Td>
              <Td className="tabular-nums">
                {it.net_kg === null
                  ? <Pill tone="amber" title="進庫未過磅">無重量帳</Pill>
                  : kgUnit(Number(it.net_kg), 1)}
              </Td>
              <Td className="tabular-nums">
                {it.panel_count === null ? '—' : (
                  <Link to={`/inventory/${it.item_id}`} className="text-sky-700">
                    {count(it.panel_count)}
                  </Link>
                )}
              </Td>
              <Td><ResolutionBadge grade={it.resolution_grade} reason={it.r3_taint ? '本料件曾以重量切分' : ''} /></Td>
              <Td><HomogeneityBadge grade={it.homogeneity_grade} reason="" /></Td>
              <Td className="text-xs">{dict.location(it.location_id)}</Td>
              <Td className="text-xs">{dict.container(it.container_id)}</Td>
            </tr>
          ))}
        </Table>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-ink-500">
        <span className="tabular-nums">合計 {kgUnit(totalKg, 1)}</span>
        {data?.nextCursor && (
          <Button
            size="sm"
            disabled={loading}
            onClick={() => {
              setPages((p) => [...p, data.items])
              setCursor(data.nextCursor)
            }}
          >{loading ? '載入中…' : '載入更多'}</Button>
        )}
      </div>
    </Card>
  )
}
